import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { EnvelopeClosedIcon } from '@radix-ui/react-icons';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { zodResolver } from '@hookform/resolvers/zod';

import { type MagicLinkFormData, magicLinkSchema } from '@/features/auth/model/schemas';

import { authService } from '@/shared/api/auth-service';
import { useOtpCooldown } from '@/shared/hooks/use-otp-cooldown';
import { logger } from '@/shared/lib/logger';
import { formatCooldown } from '@/shared/lib/otp-cooldown';
import { CustomInput, GoogleIcon } from '@/shared/ui';

type EmailStepProps = {
  onSubmit: (data: MagicLinkFormData) => Promise<void>;
  isSubmitting: boolean;
};

export const EmailStep = ({ onSubmit, isSubmitting }: EmailStepProps) => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MagicLinkFormData>({
    resolver: zodResolver(magicLinkSchema),
  });

  // A code may already be in flight to the address being typed (e.g. the user
  // reloaded mid-cooldown and is re-entering it). Reflect the persisted
  // server-side resend window so they don't fire a request that's bound to 429.
  const emailValue = (watch('email') ?? '').trim();
  const { secondsLeft, isCoolingDown } = useOtpCooldown(emailValue);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const result = await authService.signInWithGoogle();

      if (result.error) {
        logger.auth.error('Google sign in failed', { error: result.error });
        setIsGoogleLoading(false);
      }
      // If successful, the page will redirect so no need to handle success case
    } catch (error) {
      logger.auth.error('Google sign in error', { error });
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      {/* Google Login */}
      <Button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isGoogleLoading || isSubmitting}
        variant="outline"
        className="w-full mb-4 font-medium cta-primary-mobile"
      >
        {isGoogleLoading ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        Continue with Google
      </Button>

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-card text-muted-foreground">or</span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <CustomInput
          {...register('email')}
          type="email"
          label="Email"
          placeholder="Enter your email"
          error={errors.email?.message}
        />

        {isCoolingDown ? (
          <Alert role="status" className="border-primary/30 bg-primary/5 text-foreground">
            <EnvelopeClosedIcon className="h-4 w-4" />
            <AlertDescription className="text-muted-foreground">
              A code is already on its way to{' '}
              <span className="font-medium text-foreground break-words">{emailValue}</span>. Check
              your inbox and spam — you can request another in{' '}
              <span className="font-medium text-primary whitespace-nowrap">
                {formatCooldown(secondsLeft)}
              </span>
              .
            </AlertDescription>
          </Alert>
        ) : (
          <p className="text-xs text-muted-foreground -mt-1 mb-2">
            We&apos;ll send a 6-digit code to verify your email.
          </p>
        )}

        {/* The button stays enabled during the cooldown: submitEmail's
          * pre-check routes straight to code entry WITHOUT a server send, so a
          * user who already has a code (or reloaded mid-flow) isn't stranded
          * on this step until the countdown expires. The alert above carries
          * the resend wait. */}
        <Button
          type="submit"
          className="w-full min-h-[40px] cta-primary-mobile disabled:cursor-not-allowed"
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {isCoolingDown ? 'Enter the code you received' : 'Continue with Email'}
        </Button>
      </form>
    </>
  );
};
